/**
 * Validation du formulaire de création assistée — **version stricte** (décision A7, étape 2).
 *
 * COPIE de msd_portail/src/pages/creation-assisted/steps/stepValidation.js
 * Source copiée le : 2026-10-30
 * ⚠️ Toute modification doit être faite DANS LES DEUX FICHIERS.
 * La version serveur (msd_portail/functions/prospection.js) applique les mêmes règles.
 *
 * Pourquoi une copie et pas un import : deux projets npm séparés. Pourquoi la version stricte et
 * pas `validateFormForSubmission` (`msd_portail/functions/creation-requests.js`) : cette dernière
 * ne contrôle que la non-vacuité. Un formulaire accepté par la version permissive produit une
 * demande que le portail affichera de travers — c'est la stricte qui fait foi, à trois endroits :
 * la modale (bouton désactivé), `useCreateProspectAccount` (garde) et la callable (garde finale).
 *
 * Seuls ajouts par rapport au portail, tous deux sans effet sur ses règles :
 *  - `EMAIL_REGEX` / `emailValide` — l'email est exigé par A1 côté admin, le formulaire autonome
 *    ne le demande jamais (il est porté par le compte) ;
 *  - `validateStrict(form)` — la même somme de règles, mais avec des **chemins pointés**
 *    (`businessInfo.adresse.rue`) directement exploitables par la modale et par `details.fields`.
 */

export const DESIGN_DEFAULTS = { primaryColor: '#007AFF', secondaryColor: '#FF2D55' };

/** A1 — un email = un compte. Format minimal, identique à la garde 5 de la callable. */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Bornes du résumé d'activité (50 à 500 caractères, règle du portail). */
export const RESUME_MIN = 50;
export const RESUME_MAX = 500;

export const validateStep1 = (personalInfo = {}) => {
  const errors = {};
  const first = String(personalInfo.firstName || '').trim();
  const last = String(personalInfo.lastName || '').trim();
  if (first.length < 2) {
    errors.firstName = 'Le prénom est requis (2 caractères minimum)';
  }
  if (last.length < 2) {
    errors.lastName = 'Le nom est requis (2 caractères minimum)';
  }
  return errors;
};

/**
 * Étape 2 — Votre entreprise.
 * Le SIRET est **facultatif** : vide accepté, contrôlé à 14 chiffres s'il est saisi.
 * ⚠️ `adresse` est **imbriquée** dans `businessInfo` (piège n°4 de l'étape 2).
 */
export const validateStep2 = (businessInfo = {}) => {
  const errors = {};
  const adresse = businessInfo.adresse || {};

  const siret = String(businessInfo.siret || '').trim();
  if (siret && !/^\d{14}$/.test(siret)) {
    errors.siret = "Le SIRET, s'il est renseigné, doit contenir 14 chiffres";
  }
  if (String(businessInfo.nomCommercial || '').trim().length < 2) {
    errors.nomCommercial = 'Le nom commercial est requis';
  }
  const tel = String(businessInfo.telephone || '').trim().replace(/[\s.-]/g, '');
  if (!/^(\+33|0)\d{9}$/.test(tel)) {
    errors.telephone = 'Numéro de téléphone invalide (ex : 0612345678)';
  }
  if (String(adresse.rue || '').trim().length < 5) {
    errors.rue = "L'adresse est requise (5 caractères minimum)";
  }
  if (!/^\d{5}$/.test(String(adresse.codePostal || '').trim())) {
    errors.codePostal = 'Le code postal doit contenir 5 chiffres';
  }
  if (String(adresse.ville || '').trim().length < 2) {
    errors.ville = 'La ville est requise';
  }
  return errors;
};

export const validateStep3 = (activity = {}) => {
  const errors = {};
  if (String(activity.metier || '').trim().length < 3) {
    errors.metier = 'Le métier / activité est requis (3 caractères minimum)';
  }
  const resume = String(activity.resumeActivite || '').trim();
  if (resume.length < RESUME_MIN) {
    errors.resumeActivite = `Le résumé doit contenir au moins ${RESUME_MIN} caractères`;
  } else if (resume.length > RESUME_MAX) {
    errors.resumeActivite = `Le résumé ne doit pas dépasser ${RESUME_MAX} caractères`;
  }
  return errors;
};

export const FAQ_MAX_ITEMS = 3;

export const validateStep4 = (services = [], form = {}) => {
  const errors = {};
  if (!Array.isArray(services) || services.length === 0) {
    errors.services = 'Ajoutez au moins un service';
  } else {
    services.forEach((service, index) => {
      if (!service || !String(service.nom || '').trim()) {
        errors[`${index}.nom`] = 'Le nom du service est requis';
      }
      if (!service || !String(service.description || '').trim()) {
        errors[`${index}.description`] = 'La description est requise';
      }
      if (!service || service.prix === undefined || service.prix === null || String(service.prix).trim() === '') {
        errors[`${index}.prix`] = 'Le prix est requis';
      }
    });
  }

  // FAQ optionnelle : paires question/réponse complètes uniquement.
  const faq = Array.isArray(form?.faqAnswers) ? form.faqAnswers : [];
  if (faq.length > FAQ_MAX_ITEMS) {
    errors.faq = `Vous pouvez ajouter ${FAQ_MAX_ITEMS} questions maximum.`;
  } else {
    faq.forEach((entry, index) => {
      if (errors.faq) return;
      const question = String(entry?.question || '').trim();
      const answer = String(entry?.answer || '').trim();
      if ((question || answer) && (!question || !answer)) {
        errors.faq = `La question ${index + 1} doit comporter une question ET une réponse.`;
      }
    });
  }
  return errors;
};

// Pièces jointes (étape 5) : tout optionnel — aucune erreur bloquante.
export const validateStep5 = () => ({});

export const STEP_VALIDATORS = [
  validateStep1,
  validateStep2,
  validateStep3,
  validateStep4,
  validateStep5
];

/** Vrai si l'adresse a le format attendu par A1. */
export const emailValide = (email) => EMAIL_REGEX.test(String(email || '').trim().toLowerCase());

/**
 * Les cinq étapes en une seule passe, avec des **chemins pointés**.
 *
 * C'est le contrat partagé par la modale, le hook et la callable : un objet vide signifie
 * « formulaire soumissible », toute clé est un message affichable tel quel sur le champ concerné.
 *
 * L'email n'est **pas** validé ici (il ne fait pas partie de `creation_form`) : A1 est tenu par la
 * modale (`emailValide`) et par la garde 5 de la callable.
 *
 * @param {object} form formulaire au format `creation_form`
 * @returns {Record<string, string>} `{ 'businessInfo.adresse.rue': '…' }`
 */
export const validateStrict = (form = {}) => {
  const personalInfo = form?.personalInfo || {};
  const businessInfo = form?.businessInfo || {};
  const activity = form?.activity || {};

  const erreurs = {};

  const etape1 = validateStep1(personalInfo);
  if (etape1.firstName) erreurs['personalInfo.firstName'] = etape1.firstName;
  if (etape1.lastName) erreurs['personalInfo.lastName'] = etape1.lastName;

  const etape2 = validateStep2(businessInfo);
  if (etape2.siret) erreurs['businessInfo.siret'] = etape2.siret;
  if (etape2.nomCommercial) erreurs['businessInfo.nomCommercial'] = etape2.nomCommercial;
  if (etape2.telephone) erreurs['businessInfo.telephone'] = etape2.telephone;
  if (etape2.rue) erreurs['businessInfo.adresse.rue'] = etape2.rue;
  if (etape2.codePostal) erreurs['businessInfo.adresse.codePostal'] = etape2.codePostal;
  if (etape2.ville) erreurs['businessInfo.adresse.ville'] = etape2.ville;

  const etape3 = validateStep3(activity);
  if (etape3.metier) erreurs['activity.metier'] = etape3.metier;
  if (etape3.resumeActivite) erreurs['activity.resumeActivite'] = etape3.resumeActivite;

  const etape4 = validateStep4(form?.services, form);
  Object.entries(etape4).forEach(([cle, message]) => {
    // `services` (liste vide) et `faq` restent tels quels ; les erreurs de ligne deviennent
    // `services.0.nom`, `services.0.description`, `services.0.prix`.
    erreurs[cle === 'services' || cle === 'faq' ? cle : `services.${cle}`] = message;
  });

  // Étape 5 : `validateStep5 = () => ({})` — jamais bloquant (attachments, design, domaine).
  Object.entries(validateStep5()).forEach(([cle, message]) => {
    erreurs[cle] = message;
  });

  return erreurs;
};
